// React
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';

// VDS
import Chip from '@visa/vds-react/chip-styled';
import ChipText from '@visa/vds-react/chip-text-styled';

// CYBS
import CybsButton from '@cybs-ui/components/lib/button';
import styles from './component.styles';

const CybsChip = ({
  ariaLabel,
  isActive: activeProp,
  children,
  dataTest,
  disabled,
  isEditable,
  onClick,
  readOnly,
  isRemovable,
  variant,
  width,
  ...props
}) => {
  const [isActive, setActive] = useState(activeProp);
  const [chipId, setChipId] = useState(null);
  const [chipIconId, setChipIconId] = useState(null);

  /** For Accessibility - To read the text and the icon name */
  useEffect(() => {
    setChipId(shortid.generate());
    setChipIconId(shortid.generate());
  }, [children]);

  useEffect(() => {
    setActive(isActive);
  }, [isActive]);

  const handleToggle = event => {
    setActive(!isActive);
    if (typeof onClick === 'function') {
      onClick(event, !isActive);
    }
  };

  let chipIcon = null;
  const isClickable = isEditable || isRemovable;

  const commonProps = {
    'aria-labelledby': `${chipId} ${chipIconId}`,
    dataTest,
    id: chipIconId,
    resolution: 'tiny',
    variant: 'icon',
    ...props
  };

  if (isEditable) {
    chipIcon = (
      <CybsButton
        aria-expanded={Boolean(isActive)}
        ariaLabel={
          ariaLabel || (isActive ? 'arrow is collapsed' : 'arrow is expanded')
        }
        dataTest={dataTest}
        iconClass={isActive ? 'arrow-collapse' : 'arrow-expand'}
        iconType={isActive ? 'dark-tiny' : 'light-tiny'}
        onClick={handleToggle}
        {...commonProps}
      />
    );
  } else if (readOnly) {
    chipIcon = (
      <CybsButton
        ariaLabel={ariaLabel || 'Locked'}
        disabled
        iconClass="security-lock"
        iconType="light-tiny"
        {...commonProps}
      />
    );
  } else if (isRemovable) {
    chipIcon = (
      <CybsButton
        ariaLabel={ariaLabel || 'Close'}
        iconClass="close"
        iconType={isActive ? 'dark-tiny' : 'light-tiny'}
        onClick={onClick}
        {...commonProps}
      />
    );
  }

  const styleDictionary = {
    base: theme => styles(theme, isActive, isClickable).chipActive,
    columnFilter: theme => styles(theme, isActive, null, width).columnFilter
  };
  const chipStyle = styleDictionary[variant]
    ? styleDictionary[variant]
    : styleDictionary.base;

  return (
    <Chip
      css={chipStyle}
      data-test={`${dataTest}-cybsChip`}
      disabled={disabled}
      readOnly={readOnly}
    >
      <ChipText id={chipId}>{children}</ChipText>
      {chipIcon}
    </Chip>
  );
};

CybsChip.propTypes = {
  /*
   * Aria label for the chip.
   */
  ariaLabel: PropTypes.string,

  /*
   * If true, chip is toggled (open state)
   */
  isActive: PropTypes.bool,

  /*
   * Label for the chip.
   */
  children: PropTypes.string,

  /*
   * Data Test
   */
  dataTest: PropTypes.string.isRequired,

  /*
   * If true, the chip is disabled
   */
  disabled: PropTypes.bool,

  /*
   * If true, the content of the chip can change on selection of a dropdown item
   */
  isEditable: PropTypes.bool,

  /*
   * Callback fired when the chip is clicked on.
   */
  onClick: PropTypes.func,

  /*
   * If true, The chip cannot be editable.
   */
  readOnly: PropTypes.bool,

  /*
   * If true, The chip can be removed
   */
  isRemovable: PropTypes.bool,
  /**
   * Used for styling
   */
  variant: PropTypes.oneOf(['base', 'columnFilter']),
  /**
   * Used for styling
   */
  width: PropTypes.number
};

CybsChip.defaultProps = {
  ariaLabel: '',
  isActive: false,
  children: '',
  isEditable: false,
  disabled: false,
  readOnly: false,
  isRemovable: false,
  onClick: () => {},
  variant: 'base',
  width: 0
};

CybsChip.displayName = 'CybsChip';

export default CybsChip;
